/**
 * Timezone utilities: format and convert between display timezone and UTC (storage).
 * Uses dayjs with utc and timezone plugins. All functions take IANA timezone id (e.g. Asia/Kolkata).
 */

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Curated list of IANA timezone identifiers for dropdowns and validation */
export const ALLOWED_TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'America/New_York',
  'Europe/London',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney'
];

/** Static metadata for each allowed timezone (standard-time abbreviation and offset). */
const TIMEZONE_META = {
  'UTC':              { abbr: 'UTC',  long: 'Coordinated Universal Time',       offset: 'GMT+00:00' },
  'Asia/Kolkata':     { abbr: 'IST',  long: 'Indian Standard Time',             offset: 'GMT+05:30' },
  'America/New_York': { abbr: 'EST',  long: 'Eastern Standard Time',            offset: 'GMT-05:00' },
  'Europe/London':    { abbr: 'GMT',  long: 'Greenwich Mean Time',              offset: 'GMT+00:00' },
  'Europe/Paris':     { abbr: 'CET',  long: 'Central European Time',            offset: 'GMT+01:00' },
  'Asia/Dubai':       { abbr: 'GST',  long: 'Gulf Standard Time',               offset: 'GMT+04:00' },
  'Asia/Singapore':   { abbr: 'SGT',  long: 'Singapore Standard Time',          offset: 'GMT+08:00' },
  'Australia/Sydney': { abbr: 'AEST', long: 'Australian Eastern Standard Time', offset: 'GMT+10:00' },
};

/**
 * Short label for a timezone: abbreviation + offset.
 * @param {string} ianaId - IANA timezone identifier (e.g. 'Asia/Kolkata')
 * @returns {string} e.g. "IST (GMT+05:30)"
 */
export function getTimezoneShortLabel(ianaId) {
  if (!ianaId) return '';
  const meta = TIMEZONE_META[ianaId];
  if (meta) return `${meta.abbr} (${meta.offset})`;
  try {
    const date = new Date();
    const shortName = new Intl.DateTimeFormat('en-US', { timeZone: ianaId, timeZoneName: 'short' })
      .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value || ianaId;
    const offset = new Intl.DateTimeFormat('en-US', { timeZone: ianaId, timeZoneName: 'longOffset' })
      .formatToParts(date).find((p) => p.type === 'timeZoneName')?.value || '';
    return `${shortName} (${offset})`;
  } catch {
    return ianaId;
  }
}

/**
 * Long label for a timezone: full name + abbreviation + offset.
 * @param {string} ianaId - IANA timezone identifier (e.g. 'Asia/Kolkata')
 * @returns {string} e.g. "Indian Standard Time (IST) (GMT+05:30)"
 */
export function getTimezoneLabel(ianaId) {
  if (!ianaId) return '';
  const meta = TIMEZONE_META[ianaId];
  if (meta) return `${meta.long} (${meta.abbr}) (${meta.offset})`;
  return getTimezoneShortLabel(ianaId);
}

/**
 * Format a UTC date/time for display in the given timezone with label.
 * @param {string|Date} isoDate - ISO date or Date (stored as UTC)
 * @param {string} [timeStr] - Optional time "HH:mm" (if stored separately; otherwise derived from isoDate)
 * @param {string} displayTimezone - IANA timezone for display
 * @returns {string} e.g. "20 Feb 2026 11:41 Indian Standard Time (IST) (GMT+05:30)"
 */
export function formatDateTimeWithTimezone(isoDate, timeStr, displayTimezone) {
  if (!isoDate && !timeStr) return '—';
  if (!displayTimezone) return formatDateTimeWithTimezone(isoDate, timeStr, 'UTC');
  try {
    const d = dayjs(isoDate).tz(displayTimezone);
    if (!d.isValid()) return String(isoDate || '');
    const datePart = d.format('DD MMM YYYY');
    const timePart = (timeStr && String(timeStr).trim()) ? String(timeStr).trim().slice(0, 5) : d.format('HH:mm');
    const label = getTimezoneLabel(displayTimezone);
    return `${datePart} ${timePart} ${label}`.trim();
  } catch {
    return String(isoDate || '') + (timeStr ? ` ${timeStr}` : '');
  }
}

/**
 * Format a date-only value in display timezone with label.
 * @param {string|Date} isoDate - ISO date or Date (UTC)
 * @param {string} displayTimezone - IANA timezone
 * @returns {string} e.g. "20 Feb 2026 (Indian Standard Time (IST) (GMT+05:30))"
 */
export function formatDateWithTimezone(isoDate, displayTimezone) {
  if (!isoDate) return '—';
  if (!displayTimezone) return formatDateWithTimezone(isoDate, 'UTC');
  try {
    const d = dayjs(isoDate).tz(displayTimezone);
    if (!d.isValid()) return String(isoDate);
    const datePart = d.format('DD MMM YYYY');
    const label = getTimezoneLabel(displayTimezone);
    return `${datePart} (${label})`;
  } catch {
    return String(isoDate);
  }
}

/**
 * Convert stored UTC instant to display-timezone date and time for form inputs.
 * @param {string|Date} isoDate - ISO date or Date (UTC)
 * @param {string} displayTimezone - IANA timezone
 * @returns {{ date: string, time: string }} { date: 'YYYY-MM-DD', time: 'HH:mm' }
 */
export function utcToDisplayDateAndTime(isoDate, displayTimezone) {
  const fallback = { date: '', time: '00:00' };
  if (!isoDate) return fallback;
  const tz = displayTimezone || 'UTC';
  try {
    const d = dayjs(isoDate).tz(tz);
    if (!d.isValid()) return fallback;
    return {
      date: d.format('YYYY-MM-DD'),
      time: d.format('HH:mm')
    };
  } catch {
    return fallback;
  }
}

/**
 * Convert user-entered date and time (in display timezone) to UTC ISO string for API.
 * @param {string} dateStr - 'YYYY-MM-DD'
 * @param {string} timeStr - 'HH:mm' (default '00:00' if missing)
 * @param {string} displayTimezone - IANA timezone in which the user entered the value
 * @returns {string|null} ISO string in UTC or null if invalid
 */
export function displayDateTimeToUTC(dateStr, timeStr, displayTimezone) {
  if (!dateStr || !String(dateStr).trim()) return null;
  const tz = displayTimezone || 'UTC';
  const date = String(dateStr).trim().slice(0, 10);
  const time = (timeStr && String(timeStr).trim()) ? String(timeStr).trim().slice(0, 5) : '00:00';
  try {
    const d = dayjs.tz(`${date}T${time}:00`, tz);
    if (!d.isValid()) return null;
    return d.utc().toISOString();
  } catch {
    return null;
  }
}
